import convertPressArticleToCardProp from '@/helpers/convertPressArticleToCardProp';
import usePressArticles from '@/hooks/usePressArticles/usePressArticles';
import { useSelected } from '@/hooks/useSelected/useSelected';
import useShowMore from '@/hooks/useShowMore/useShowMore';
import AccordionItem from '@uikit/components/AccordionItem/AccordionItem';
import Button from '@uikit/components/Button/Button';
import { ButtonAlign } from '@uikit/components/Button/consts';
import { ClickableAction } from '@uikit/components/Clickable/consts';
import { ContainerChildrenSpace } from '@uikit/components/Container/consts';
import Container from '@uikit/components/Container/Container';
import { FormFieldType } from '@uikit/components/FormField/consts';
import FormField from '@uikit/components/FormField/FormField';
import { FormFieldElement } from '@uikit/components/FormField/interfaces';
import MultiSelect from '@uikit/components/MultiSelect/MultiSelect';
import Pill from '@uikit/components/Pill/Pill';
import PillsWrapper from '@uikit/components/PillsWrapper/PillsWrapper';
import FilterResetButton from '@uikit/components/Press/FilterResetButton/FilterResetButton';
import FilterWrapper from '@uikit/components/Press/FilterWrapper/FilterWrapper';
import PressCheckBoxList from '@uikit/components/Press/PressCheckBoxList/PressCheckBoxList';
import PressFilterModal from '@uikit/components/Press/PressFilterModal/PressFilterModal';
import PressGrid from '@uikit/components/PressGrid/PressGrid';
import { SectionVariant } from '@uikit/components/Section/consts';
import Section from '@uikit/components/Section/Section';
import React, { useState } from 'react';
import uniqueArray from '../../../helpers/uniqueArray';
import type { MultiSelectLabel } from '../MultiSelect/interfaces';
import type { PressFilterProps } from './interfaces';

const PRESS_OVERVIEW_PAGE_CONFIG = {
  default: {
    showTagsFilter: true,
    showMediaTypesFilter: true,
  },
  constructions: {
    showTagsFilter: false,
    showMediaTypesFilter: false,
  },
};

const getPressPageConfig = (pressType?: string) => {
  if (
    typeof pressType === 'string' &&
    pressType in PRESS_OVERVIEW_PAGE_CONFIG
  ) {
    return PRESS_OVERVIEW_PAGE_CONFIG[
      pressType as keyof typeof PRESS_OVERVIEW_PAGE_CONFIG
    ];
  }
  return PRESS_OVERVIEW_PAGE_CONFIG.default;
};

const PressFilter = ({
  children,
  pressType,
  mediaTypes = [],
  initialPressArticles = [],
  tags,
}: PressFilterProps) => {
  const { showTagsFilter, showMediaTypesFilter } =
    getPressPageConfig(pressType);

  const [searchTerm, setSearchTerm] = useState('');

  const { data } = usePressArticles(pressType);

  const pressArticles = data?.length ? data : initialPressArticles;

  const selectedCategories = useSelected();
  const selectedYears = useSelected();
  const selectedMediaTypes = useSelected();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const toggleFilterModal = () => {
    setIsFilterModalOpen(!isFilterModalOpen);
  };

  const years = uniqueArray(
    pressArticles
      .filter(({ publicationDate }) => publicationDate)
      .map((article) =>
        new Date(article?.publicationDate ?? '').getFullYear().toString()
      )
  );

  const filteredItems = pressArticles
    .filter((article) => {
      return (
        searchTerm.length === 0 ||
        article.title?.toLowerCase().includes(searchTerm) ||
        article.teaserText?.toLowerCase().includes(searchTerm)
      );
    })
    .filter((article) => {
      return (
        selectedCategories.numberOfSelected === 0 ||
        selectedCategories.isSelected(article.mainTag ?? '') ||
        article.tags?.some((tag) => selectedCategories.isSelected(tag))
      );
    })
    .filter((article) => {
      return (
        selectedMediaTypes.numberOfSelected === 0 ||
        selectedMediaTypes.isSelected(article.mediaType ?? '')
      );
    })
    .filter((article) => {
      return (
        selectedYears.numberOfSelected === 0 ||
        selectedYears.isSelected(
          new Date(article.publicationDate ?? '').getFullYear().toString()
        )
      );
    });

  const {
    canGoNext,
    next,
    currentPageItems: items,
  } = useShowMore(filteredItems, {
    itemsPerPage: 6,
  });

  const onSearchTermChange = ($event: React.ChangeEvent<FormFieldElement>) => {
    const { value } = $event.target;
    setSearchTerm(value?.toLowerCase());
  };

  const onFilterReset = () => {
    selectedCategories.resetSelected();
    selectedYears.resetSelected();
    selectedMediaTypes.resetSelected();
  };

  const tagLabels: MultiSelectLabel[] = tags.map((tag) => {
    return {
      label: tag,
    };
  });

  const mediaTypeLabels: MultiSelectLabel[] = mediaTypes.map((mediaType) => {
    return {
      label: mediaType,
    };
  });

  const yearLabels: MultiSelectLabel[] = years.map((year) => {
    return {
      label: year,
    };
  });

  const onPillClick = (type: string, selectedItem: string) => {
    if (type === 'category') {
      selectedCategories.toggleSelected(selectedItem);
    }

    if (type === 'years') {
      selectedYears.toggleSelected(selectedItem);
    }

    if (type === 'mediaType') {
      selectedMediaTypes.toggleSelected(selectedItem);
    }
  };

  const headlineWithAmount = (text: string, amount: number): string => {
    return `${text} ${amount > 0 ? `(${amount})` : ''}`;
  };

  const selectedItemCount =
    selectedCategories.numberOfSelected +
    selectedMediaTypes.numberOfSelected +
    selectedYears.numberOfSelected;

  return (
    <>
      <Section variant={SectionVariant.Default}>
        <Container spaceBetweenChildren={ContainerChildrenSpace.None}>
          <FilterWrapper
            selectedItemCount={selectedItemCount}
            onFilterClick={toggleFilterModal}
          >
            <FormField
              name="searchTerm"
              label={'Suchbegriff...'}
              type={FormFieldType.Text}
              onChange={onSearchTermChange}
              icon={'action/basic-search'}
            />
            {showTagsFilter && (
              <MultiSelect
                label="Themen"
                items={tagLabels}
                {...selectedCategories}
              />
            )}
            {showMediaTypesFilter && (
              <MultiSelect
                label="Medientypen"
                items={mediaTypeLabels}
                {...selectedMediaTypes}
              />
            )}
            <MultiSelect label="Jahre" items={yearLabels} {...selectedYears} />
          </FilterWrapper>
          {selectedItemCount > 0 && (
            <PillsWrapper>
              {selectedCategories.selected.map((category) => (
                <Pill
                  key={category}
                  title={category}
                  onClose={() => onPillClick('category', category)}
                />
              ))}

              {selectedYears.selected.map((years) => (
                <Pill
                  key={years}
                  title={years}
                  onClose={() => onPillClick('years', years)}
                />
              ))}
              {selectedMediaTypes.selected.map((mediaType) => (
                <Pill
                  key={mediaType}
                  title={mediaType}
                  onClose={() => onPillClick('mediaType', mediaType)}
                />
              ))}
              {selectedItemCount > 0 && (
                <FilterResetButton
                  title="Alle Filter zurücksetzen"
                  onClick={onFilterReset}
                />
              )}
            </PillsWrapper>
          )}
        </Container>
      </Section>

      <Section variant={SectionVariant.Default}>
        <Container>
          <PressGrid items={items.map(convertPressArticleToCardProp)} />
          {children}
        </Container>

        {canGoNext && (
          <Container>
            <Button
              actionType={ClickableAction.Custom}
              onClick={next}
              align={ButtonAlign.Center}
            >
              Mehr anzeigen
            </Button>
          </Container>
        )}
      </Section>

      <PressFilterModal
        visible={isFilterModalOpen}
        headline="Filtereinstellungen"
        onClose={toggleFilterModal}
        onFilterReset={onFilterReset}
      >
        {tags && (
          <AccordionItem
            headline={headlineWithAmount(
              'Themen',
              selectedCategories.numberOfSelected
            )}
          >
            <PressCheckBoxList
              items={tags}
              selectedItems={selectedCategories.selected}
              onClick={selectedCategories.toggleSelected}
            />
          </AccordionItem>
        )}
        <AccordionItem
          headline={headlineWithAmount(
            'Medientyp',
            selectedMediaTypes.numberOfSelected
          )}
        >
          <PressCheckBoxList
            items={mediaTypes}
            selectedItems={selectedMediaTypes.selected}
            onClick={selectedMediaTypes.toggleSelected}
          />
        </AccordionItem>
        <AccordionItem
          headline={headlineWithAmount('Jahre', selectedYears.numberOfSelected)}
        >
          <PressCheckBoxList
            items={years}
            selectedItems={selectedYears.selected}
            onClick={selectedYears.toggleSelected}
          />
        </AccordionItem>
      </PressFilterModal>
    </>
  );
};

export default PressFilter;
